import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, OverlayTrigger, Tooltip, Badge, Dropdown } from 'react-bootstrap';
import ProductImage from '../ProductImage/ProductImage';
import ProdutoInformacao from '../ProdutoInformacao/ProdutoInformacao';
import TaskModal from '../TaskModal/TaskModal';
import styles from './ProductTable.module.css';

const ProductTable = memo(({ produtos, tipo, onSort, sortConfig, onFilterInfo, infoFilter, onOpenTaskModal, modoListaCompras, onClicarProduto, estaNoCarrinho, getQuantidade, codigosAlerta }) => {
  const isEcommerce = tipo === 'ecommerce';
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeProduct, setActiveProduct] = useState(null);

  const handleMouseEnter = (event, produto) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 340;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const margin = 10;

    let left = rect.left - tooltipWidth - 15;
    if (left < margin) {
      left = rect.right + 15;
    }
    if (left + tooltipWidth > windowWidth - margin) {
      left = windowWidth - tooltipWidth - margin;
    }

    const maxTooltipHeight = Math.floor(windowHeight * 0.9);
    let top = margin;
    if (rect.top < windowHeight / 2) {
      top = Math.max(margin, rect.top);
    } else {
      top = Math.max(margin, windowHeight - maxTooltipHeight - margin);
    }

    setTooltipPosition({
      top,
      left,
      maxHeight: maxTooltipHeight
    });
    setActiveProduct(produto);
  };

  const handleMouseLeave = () => {
    setActiveProduct(null);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const getImageSrc = (produto) => {
    if (isEcommerce) {
      return produto.imagemURL;
    } else {
      return produto.imagemBase64;
    }
  };

  const handleSort = (field) => {
    if (onSort) {
      onSort(field);
    }
  };

  const getSortIcon = (field) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>⇅</span>;
    }
    return sortConfig.direction === 'asc'
      ? <span className="text-primary ms-1">↑</span>
      : <span className="text-primary ms-1">↓</span>;
  };

  const handleFilterInfo = () => {
    if (onFilterInfo) {
      onFilterInfo();
    }
  };

  const getFilterIcon = () => {
    if (!infoFilter || infoFilter === 'all') {
      return <span className="ms-1">🔍</span>;
    }
    if (infoFilter === 'recorrente') {
      return <span className="ms-1" title="Filtrando: Recorrentes">🔄</span>;
    }
    if (infoFilter === 'nao_comprar') {
      return <span className="ms-1" title="Filtrando: Não Comprar">🚫</span>;
    }
    return <span className="ms-1">🔍</span>;
  };

  const getTableRowClassName = (produto) => {
    const isPromo = (isEcommerce ? produto.nome : produto.descricao)?.includes('**');
    return isPromo ? 'table-warning' : '';
  };

  const getRowStyle = (produto) => {
    const codigo = isEcommerce ? produto.codigo : produto.codigoInterno;
    const temAlerta = codigosAlerta && codigosAlerta.size > 0 && codigosAlerta.has(String(codigo || ''));
    // Se tem alerta, o estilo de fundo e borda lateral são aplicados via classe CSS (com animação)
    if (temAlerta) {
      return { cursor: modoListaCompras ? 'pointer' : 'default' };
    }
    if (!modoListaCompras) return {};
    const noCarrinho = estaNoCarrinho && estaNoCarrinho(codigo);
    if (noCarrinho) return { background: 'rgba(34, 197, 94, 0.06)', boxShadow: 'inset 3px 0 0 #22c55e', cursor: 'pointer' };
    return { cursor: 'pointer' };
  };

  return (
    <>
      <Table hover responsive className="mb-0 align-middle">
        <thead className="bg-light">
          <tr>
            <th className="text-center" style={{ width: '80px' }}>Imagem</th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('codigo')}>
              Código {getSortIcon('codigo')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('descricao')}>
              Descrição {getSortIcon('descricao')}
            </th>
            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('vendasMesAtual')}>
              Vendas Mês {getSortIcon('vendasMesAtual')}
            </th>
            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort(isEcommerce ? 'preco' : 'precoVenda')}>
              Preço Venda {getSortIcon(isEcommerce ? 'preco' : 'precoVenda')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('precoCusto')}>
              Preço Custo {getSortIcon('precoCusto')}
            </th>
            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('lucro')}>
              Lucro {getSortIcon('lucro')}
            </th>
            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('estoque')}>
              Estoque {getSortIcon('estoque')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('fornecedor')}>
              Fornecedor {getSortIcon('fornecedor')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('categoria')}>
              Categoria {getSortIcon('categoria')}
            </th>
            <th className="text-center">Info</th>
            <th className="text-center">{modoListaCompras ? '+ Lista' : 'Ações'}</th>
          </tr>
        </thead>
        <tbody>
          {produtos.map((produto) => {
            const codKey = isEcommerce ? produto.id : produto.codigoInterno;
            const codProd = isEcommerce ? produto.codigo : produto.codigoInterno;
            const noCarrinho = modoListaCompras && estaNoCarrinho && estaNoCarrinho(codProd);
            const qtdCarrinho = noCarrinho && getQuantidade ? getQuantidade(codProd) : 0;

            return (
            <tr
              key={codKey}
              className={[
                getTableRowClassName(produto),
                (codigosAlerta && codigosAlerta.size > 0 && codigosAlerta.has(String(codProd || ''))) ? styles.linhaAlertaRuptura : ''
              ].filter(Boolean).join(' ')}
              style={getRowStyle(produto)}
              onClick={modoListaCompras ? () => onClicarProduto && onClicarProduto(produto) : undefined}
            >
              <td
                className="text-center"
                onMouseEnter={(e) => handleMouseEnter(e, produto)}
                onMouseLeave={handleMouseLeave}
              >
                <div style={{ width: '50px', height: '50px', margin: '0 auto' }}>
                  <ProductImage
                    src={getImageSrc(produto)}
                    alt={isEcommerce ? produto.nome : produto.descricao}
                    className="img-fluid rounded"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </td>
              <td
                className="fw-bold"
                onMouseEnter={(e) => handleMouseEnter(e, produto)}
                onMouseLeave={handleMouseLeave}
              >
                {isEcommerce ? produto.codigo : produto.codigoInterno}
              </td>
              <td
                onMouseEnter={(e) => handleMouseEnter(e, produto)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="text-truncate" style={{ maxWidth: '200px' }}>
                  {isEcommerce ? produto.nome : produto.descricao}
                </div>
                {!isEcommerce && produto.descricaoResumida && (
                  <small className="text-muted d-block text-truncate" style={{ maxWidth: '200px' }}>
                    {produto.descricaoResumida}
                  </small>
                )}
              </td>
              <td className="text-center">
                <span className={`badge ${(produto.vendasMesAtual || 0) > 0 ? 'bg-info' : 'bg-secondary'}`}>
                  {produto.vendasMesAtual || 0}
                </span>
              </td>
              {/* Coluna: Preço Venda (Unitário) */}
              <td className="text-center">
                <span className="fw-bold text-success">
                  {isEcommerce
                    ? (produto.preco ? formatarMoeda(produto.preco) : '-')
                    : (
                      <div title={`Origem: ${produto.tipoPreco}`}>
                        {produto.precoVenda ? formatarMoeda(produto.precoVenda) : '-'}
                        {produto.tipoPreco && (
                          <span className="badge bg-secondary ms-1" style={{ fontSize: '0.6em' }}>
                            {produto.tipoPreco.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    )
                  }
                </span>
              </td>
              <td>{produto.precoCusto > 0 ? formatarMoeda(produto.precoCusto) : '-'}</td>
              <td className="text-center">
                {produto.lucro ? (
                  <span className={produto.lucro >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                    {formatarMoeda(produto.lucro)}
                  </span>
                ) : '-'}
              </td>
              <td className="text-center">
                <span className={`badge ${produto.estoque > 0 ? 'bg-success' : 'bg-danger'}`}>
                  {produto.estoque > 0 ? produto.estoque : 0}
                </span>
              </td>
              <td>{produto.fornecedor || '-'}</td>
              <td>{produto.categoria || '-'}</td>
              <td className="text-center">
                <ProdutoInformacao
                  produtoCodigo={isEcommerce ? produto.codigo : produto.codigoInterno}
                  valorAtual={produto.informacao}
                  tipo={tipo}
                />
              </td>
              <td className="text-center">
                {modoListaCompras ? (
                  noCarrinho ? (
                    <span style={{
                      background: 'linear-gradient(135deg, #16a34a, #15803d)',
                      color: '#fff',
                      borderRadius: '12px',
                      padding: '3px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}>✓ {qtdCarrinho} un.</span>
                  ) : (
                    <span style={{
                      background: 'rgba(29, 78, 216, 0.1)',
                      border: '1px dashed rgba(29, 78, 216, 0.4)',
                      color: '#1d4ed8',
                      borderRadius: '12px',
                      padding: '3px 10px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}>+ Adicionar</span>
                  )
                ) : (
                <Dropdown>
                  <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${produto.id}`} className="btn-sm border-0">
                    <i className="fas fa-ellipsis-v"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item as={Link} to={`/${tipo}/${isEcommerce ? produto.codigo : produto.codigoInterno}`}>
                      <i className="fas fa-eye me-2"></i> Ver Detalhes
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onOpenTaskModal && onOpenTaskModal(produto)}>
                      <i className="fas fa-tasks me-2"></i> Observações
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                )}
              </td>
            </tr>
          );
          })}
        </tbody>
      </Table >

      {/* Unified Tooltip */}
      {
        activeProduct && (
          <div
            className={styles.tooltip}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              maxHeight: tooltipPosition.maxHeight ? `${tooltipPosition.maxHeight}px` : '85vh',
              display: 'block' // Ensure it's visible
            }}
          >
            <div className={styles.tooltipImageContainer}>
              <ProductImage
                src={getImageSrc(activeProduct)}
                alt={isEcommerce ? activeProduct.nome : activeProduct.descricao}
                className={styles.tooltipMainImage}
              />
            </div>

            <div className={styles.tooltipHeader}>
              {isEcommerce ? activeProduct.nome : activeProduct.descricao}
            </div>

            <div className={styles.tooltipContent}>
              {/* Seção de Entrada - Compacta (igual E-commerce e Loja Física) */}
              {(activeProduct.ultimaEntrada || activeProduct.dataCadastro) && (
                <div style={{
                  marginBottom: '8px',
                  padding: '6px 10px',
                  background: 'rgba(46, 204, 113, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(46, 204, 113, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px'
                }}>
                  <span style={{ color: '#2ecc71', fontWeight: 600 }}>📦 Entrada:</span>
                  <span style={{ color: '#1e8449', fontWeight: 700 }}>
                    {new Date(activeProduct.ultimaEntrada || activeProduct.dataCadastro).toLocaleDateString('pt-BR')}
                  </span>
                  {(activeProduct.qtdEntrada12m > 0 || activeProduct.qtdChegada12m > 0) && (
                    <span style={{ color: '#27ae60', fontSize: '10px' }}>
                      (+{activeProduct.qtdEntrada12m || activeProduct.qtdChegada12m} un.)
                    </span>
                  )}
                </div>
              )}
              {activeProduct.vendasMensais && activeProduct.vendasMensais.length > 0 ? (
                <>
                  <div className={styles.tooltipSubHeader}>
                    Histórico de Vendas
                  </div>
                  {activeProduct.vendasMensais.map((venda, index) => {
                    const nextVenda = activeProduct.vendasMensais[index + 1];
                    let percentChange = null;

                    if (nextVenda) {
                      if (nextVenda.quantidade > 0) {
                        percentChange = ((venda.quantidade - nextVenda.quantidade) / nextVenda.quantidade) * 100;
                      } else if (venda.quantidade > 0) {
                        percentChange = 100;
                      } else {
                        percentChange = 0;
                      }
                    }

                    return (
                      <div key={index} className={`${styles.tooltipRow} ${index % 2 !== 0 ? styles.rowStriped : ''}`}>
                        <span className={styles.tooltipLabel}>{venda.mes}</span>
                        <div className={styles.tooltipRightSide}>
                          {percentChange !== null && Math.abs(percentChange) > 0 && (
                            <span className={`${styles.percentChange} ${percentChange >= 0 ? styles.percentPositive : styles.percentNegative}`}>
                              {percentChange > 0 ? '▲' : '▼'} {Math.abs(percentChange).toFixed(0)}%
                            </span>
                          )}
                          <span className={styles.tooltipValue}>{venda.quantidade}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className={styles.tooltipDividerStrong}></div>
                  <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Média Mensal</span>
                    <span className={styles.tooltipValue}>{activeProduct.mediaMensal || 0}</span>
                  </div>
                  {activeProduct.estoque > 0 && (
                    <div className={styles.tooltipRow}>
                      <span className={styles.tooltipLabel}>Média Diária</span>
                      <span className={styles.tooltipValue}>
                        {/* Média diária = total 90d ÷ dias-com-venda (backend) */}
                        {activeProduct.mediaDiariaAjustada > 0
                          ? activeProduct.mediaDiariaAjustada.toFixed(1)
                          : '—'}
                      </span>
                    </div>
                  )}
                  <div className={styles.tooltipDivider}></div>
                  <div className={styles.tooltipInfo}>
                    <strong>Est. Mín: {activeProduct.estoqueMinimo}</strong><br />
                    <small>(Média Mensal × 1,5)</small>
                  </div>
                  {activeProduct.estoque > 0 && (
                    <div className={styles.tooltipInfoSecondary}>
                      <small>
                        Duração estimada: <strong>
                          {activeProduct.mediaMensal > 0
                            ? Math.floor(activeProduct.estoque / (activeProduct.mediaMensal / 30))
                            : '∞'
                          } dias
                        </strong>
                      </small>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.tooltipInfo}>
                  Sem dados de vendas disponíveis
                </div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
});

ProductTable.displayName = 'ProductTable';

export default ProductTable;
